/**
 * Shared module constants and helpers.
 * Single source of truth — consumed by TenantManagement, AdminTenantsTable,
 * SubscriptionPlansManagement, TenantDetails, AccountSubscription, ModuleDisplay.
 *
 * NOTE: `services/tenantApi.ts` re-exports these symbols for backward compatibility
 * with existing imports. New consumers should import directly from `lib/modules`.
 */

// Available modules with hierarchical structure
export const MODULE_STRUCTURE = {
  hotel: {
    label: "Hotel",
    modules: ["hotel:offers", "hotel:manage"] as const,
    wildcard: "hotel:*",
  },
  grants: {
    label: "Dotacje",
    modules: ["grants:schedule", "grants:calls", "grants:kfs", "grants:bur", "grants:deminimis"] as const,
    wildcard: "grants:*",
  },
  "api-keys": {
    label: "Klucze API",
    modules: ["api-keys:manage"] as const,
    wildcard: "api-keys:*",
  },
  "admin:kiedynabory": {
    label: "KiedyNabory Admin",
    modules: [
      "admin:kiedynabory:read",
      "admin:kiedynabory:write",
      "admin:kiedynabory:rozliczenia-paulina",
      "admin:kiedynabory:rozliczenia-tenants",
    ] as const,
    wildcard: "admin:kiedynabory:*",
  },
} as const;

// Human-readable module display names
// Used across the app for consistent module naming
export const MODULE_DISPLAY_NAMES: Record<string, string> = {
  "hotel:offers": "Kalkulator ofert grupowych",
  "hotel:manage": "Zarządzanie hotelem",
  "grants:schedule": "Nabory KFS i BUR",
  "grants:calls": "Wszystkie nabory",
  "grants:deminimis": "Formularze de minimis",
  "grants:kfs": "KFS — Krajowy Fundusz Szkoleniowy",
  "grants:bur": "BUR — Baza Usług Rozwojowych",
  "hotel:*": "Hotel (wszystkie funkcje)",
  "grants:*": "Dotacje (wszystkie funkcje)",
  "api-keys:manage": "Zarządzanie kluczami API",
  "api-keys:*": "Klucze API (wszystkie funkcje)",
  "admin:kiedynabory:read": "KiedyNabory - Odczyt",
  "admin:kiedynabory:write": "KiedyNabory - Zapis",
  "admin:kiedynabory:rozliczenia-paulina": "KiedyNabory - Rozliczenia Paulina",
  "admin:kiedynabory:rozliczenia-tenants": "KiedyNabory - Rozliczenia Tenantów",
  "admin:kiedynabory:*": "KiedyNabory Admin (wszystkie)",
};

/**
 * Get human-readable display name for a module
 * @param moduleKey - Module identifier (e.g., "hotel:offers", "grants:schedule")
 * @returns Human-readable name in Polish or the original key if not found
 */
export const getModuleDisplayName = (moduleKey: string): string => {
  return MODULE_DISPLAY_NAMES[moduleKey] || moduleKey;
};

// Flat list of all available modules
export const AVAILABLE_MODULES = [
  "hotel:offers",
  "hotel:manage",
  "grants:schedule",
  "grants:calls",
  "grants:kfs",
  "grants:bur",
  "grants:deminimis",
  "hotel:*",
  "grants:*",
  "api-keys:manage",
  "api-keys:*",
  "admin:kiedynabory:read",
  "admin:kiedynabory:write",
  "admin:kiedynabory:rozliczenia-paulina",
  "admin:kiedynabory:rozliczenia-tenants",
  "admin:kiedynabory:*",
] as const;

export type ModuleName = (typeof AVAILABLE_MODULES)[number];

// Helper functions for module management
export const getModulesByCategory = () => MODULE_STRUCTURE;

export const isWildcardModule = (module: string): boolean => {
  return module.endsWith(":*");
};

export const getWildcardBase = (module: string): string => {
  return module.replace(":*", "");
};

export const getSubmodulesForWildcard = (wildcardModule: string): string[] => {
  const base = getWildcardBase(wildcardModule);
  const category = MODULE_STRUCTURE[base as keyof typeof MODULE_STRUCTURE];
  return category ? [...category.modules] : [];
};

/**
 * Check if a module is accessible given a list of enabled modules.
 *
 * Supports multi-segment wildcards. For module `a:b:c:read`, this checks
 * (in order, from most-specific to least-specific):
 *   - exact match `a:b:c:read`
 *   - `a:b:c:*`
 *   - `a:b:*`
 *   - `a:*`
 *
 * Example: `enabledModules = ["admin:kiedynabory:*"]` matches
 * `hasModuleAccess(..., "admin:kiedynabory:read")` → true.
 */
export const hasModuleAccess = (
  enabledModules: string[],
  moduleToCheck: string
): boolean => {
  // Direct match
  if (enabledModules.includes(moduleToCheck)) return true;

  // Check progressively shorter wildcard prefixes (most specific first).
  // For "a:b:c:read" we try "a:b:c:*", "a:b:*", "a:*" in that order.
  const parts = moduleToCheck.split(":");
  for (let i = parts.length - 1; i >= 1; i--) {
    const wildcardModule = parts.slice(0, i).join(":") + ":*";
    if (enabledModules.includes(wildcardModule)) return true;
  }
  return false;
};
